/**
 * Registry of routes that participate in analytics/experiments.
 *
 * Single source of truth for which routes are `cdn-shared` and which
 * edge-bucketed experiments run on each. All three consumers read it
 * directly — no derivation, no codegen:
 *
 *   - Route files: `staticData: { analytics: entry }` and
 *     `headers: cacheHeadersFor(entry)`.
 *   - `__root.tsx` `beforeLoad`: matches the request pathname against
 *     each `cdn-shared` entry's `pattern` to decide whether to skip
 *     `Analytics.Bootstrap` (cdn-shared responses must NOT bake per-user
 *     state).
 *   - The CloudFront viewer-request handler: imports this const directly;
 *     esbuild inlines the literal into the ≤10 KB edge bundle.
 *
 * **`pattern` is hand-authored, not derived from the route-id key.** We
 * treat URL-path syntax as the contract and let TanStack's path
 * conventions evolve independently. The parity test in
 * `test/unit/analytics/experimental-routes.test.ts` round-trips each
 * pattern through TanStack's own `interpolatePath` to catch drift if
 * their syntax changes under us. See the "Why hand-authored patterns"
 * note in `docs/projects/active/a-b-test-public-doc.md`.
 *
 * Adding a new experimental route is one entry below + two lines in the
 * route file (`staticData` + `headers`). Adding a new edge-bucketed
 * experiment to an existing route is one flag key in its `experiments`
 * array (plus the flag definition itself in
 * `@listening/frontend/analytics/flags/client` and its PostHog IaC files).
 *
 * **What an entry costs is declared, not discovered.** Every experiment on a
 * route multiplies the number of CloudFront cache entries that path fragments
 * into; {@link cacheEntriesPerPath} is that number and
 * {@link MAX_CACHE_ENTRIES_PER_PATH} is the ceiling, enforced over the live
 * registry by `test/unit/analytics/route-cache-budget.test.ts`. This replaces
 * the "hard cap: 3 concurrent experiments per route" rule from
 * `docs/projects/complete/a-b-test-public-doc.md` §2, which counted the wrong
 * thing: one six-way price test is a bigger cache cost than three binary ones
 * and used to look like a third of the budget.
 */
// NOTHING may be imported here that the CloudFront Functions bundle cannot
// afford. This module is reached by the edge entry via
// `edgeExperimentsByPathPattern`, and rolldown cannot tree-shake object
// properties — so anything referenced from the `edgeExperimentRoutes` literal
// ships to the edge whether or not the edge evaluates it. Route *exclusions*
// live in `./experimental-route-exclusions` for exactly this reason: their
// slug alternation was 375 bytes of an enforced 3,674-byte budget
// (`packages/infra/src/web/edge.ts`), spent on a value the edge
// never reads. Cache-only entries sit in their own literal below for the
// same reason.
import type { FileRoutesById } from '@/routeTree.gen';
import type {
  CacheMode,
  EdgeBucketedFlagKey,
  RouteAnalytics,
} from '@listening/frontend/analytics/define-route-analytics';
// Value import, but zero bytes: `handler-request.ts` already imports this exact
// module, and the only thing here that reads it —
// `edgeExperimentCacheShape` — is dropped from the edge bundle with the rest of
// the budget block. Import `flags/edge-client`, never `flags/client`, for the
// reason the header states.
import { EdgeClientFlags } from '@listening/frontend/analytics/flags/edge-client';
import type { EdgeFeatureFlagDef } from '@listening/frontend/analytics/flags/types';
import { NON_BASE_LOCALE_PREFIXES } from './locale-prefixes';

type RouteAnalyticsEntry<M extends CacheMode = CacheMode> =
  RouteAnalytics<M> & {
    readonly pattern: RegExp;
  };

/**
 * An `edgeExperimentRoutes` entry, which is a `RouteAnalyticsEntry` MINUS its
 * `cacheMode` — the one field on that literal the edge pays for and can never
 * read anything but `'cdn-shared'` from.
 *
 * Every entry there is cdn-shared by construction: an edge-bucketed variant is
 * only meaningful as a cache-key dimension, and `RouteAnalytics`'s conditional
 * type only admits `EdgeBucketedFlagKey`s under `cdn-shared` anyway. Writing
 * the constant onto each entry (and re-checking it in the
 * `edgeExperimentsByPathPattern` filter below) measured 120 bytes of the
 * 3,674-byte budget spent restating something the type system already knows —
 * which is most of what registering `/pricing` costs. `withCdnShared` puts the
 * field back when `experimentalRoutes` is composed, where nothing pays for it,
 * the same trick `edge-client-flags.ts` uses to compose full flag defs on top
 * of the edge-visible `EdgeBucketing` literal.
 *
 * `experiments` is a NON-EMPTY tuple: an entry with no experiment belongs in
 * `cacheOnlyExperimentalRoutes`, and requiring it here is what lets the
 * derivation below drop its `length > 0` runtime filter too.
 */
type EdgeExperimentRouteEntry = {
  readonly pattern: RegExp;
  readonly experiments: readonly [
    EdgeBucketedFlagKey,
    ...EdgeBucketedFlagKey[],
  ];
};

/**
 * Widen a base-locale path pattern to also match its locale-prefixed forms.
 *
 * Without this, `/es/academic` matches no entry: those visitors are never
 * bucketed, so they all fall through to the flag default (silently stacking
 * the experiment toward control), and a visitor who switches language
 * mid-funnel **flips arms** — which corrupts their experience and their row in
 * the analysis, not just the sample size.
 *
 * Flags are hardcoded to `i` rather than read off `pattern.flags`: every
 * pattern here is case-insensitive, and `RegExp.prototype.flags` is one more
 * ES surface to bet on inside the cloudfront-js-2.0 runtime, which already
 * rejects `for-of`, spread and `Map`.
 */
function withLocalePrefix(pattern: RegExp): RegExp {
  const prefix = `^(?:/(?:${NON_BASE_LOCALE_PREFIXES.join('|')}))?`;
  return new RegExp(pattern.source.replace(/^\^/, prefix), 'i');
}

// Shared, not repeated, because both of its routes ship to the edge and the
// arrays are identical: `edgeExperimentsByPathPattern` copies with `.slice()`,
// so one array behind two entries cannot be mutated into disagreement.
const PRICING_TEST_ONLY = ['pricingTestV2'] as const;

/**
 * Entries with at least one edge experiment — the ONLY entries the CloudFront
 * bundle carries. `edgeExperimentsByPathPattern` derives from this literal
 * directly, so experiment-less entries never spend edge-bundle bytes (the four
 * cache-only entries below cost ~360 bytes of the 3,674-byte budget when the
 * edge reached them through the combined literal).
 *
 * No `cacheMode` here — see {@link EdgeExperimentRouteEntry}. It is re-added
 * when `experimentalRoutes` is composed below.
 */
const edgeExperimentRoutes = {
  // Homepage — `homepage-cta-funnel-v1` changes only where the page's signup
  // CTAs point (`/app/continue` → `/onboarding/welcome`); the content, copy
  // and layout are identical in both arms. The flag is read in the route
  // component, not in `Homepage25Body`, which also renders as the academic
  // experiment's control arm.
  //
  // `pricing-test-v2` is deliberately NOT here: the homepage renders no price.
  // `marketingDisplayPricing`'s successor (`marketingPricingForArm`) has three
  // consumers — `PricingHero`, `PricingCards` and `features-card` — and
  // `Homepage25Body` mounts none of them, so registering the price test here
  // would double the highest-traffic path's cache entries (4 → 8) to change
  // nothing on the page.
  '/_marketing/': {
    pattern: withLocalePrefix(/^\/?$/i),
    experiments: ['homepageCtaFunnelV1'],
  },
  // `/pricing` — the one anonymous surface whose whole subject is the price,
  // so it is where the price test has to render. Registering it makes the
  // route `cdn-shared`, which is new: `cached-routes.ts` excluded it until now
  // because `PricingCards` swaps the ENTIRE tier set on the free-plan country
  // bit. That exclusion is lifted by CARRYING the bit — `/pricing` joins
  // `COUNTRY_KEYED_MARKETING_PATHS` in the same change, so there are two
  // correct documents rather than one wrong one. The cost is the product of
  // both dimensions: 2 × 2 = 4 entries under `pricing-test-v2`; v1's six arms
  // made it 12, which is what moved `MAX_CACHE_ENTRIES_PER_PATH` to 12.
  //
  // `academic-landing-funnel-v1` is NOT added here and the pricing test is not
  // added to `/academic` — those pages render no price, and cross-registering
  // would cost both paths entries for nothing.
  //
  // Of the six OTHER price-rendering anonymous routes — `/audio`, `/youtube`,
  // `/college`, `/edu`, `/newsletter`, `/ref`, the `FeaturesCardSection`
  // consumers — only `/audio` is registered (below). Two independent limits
  // picked the same one:
  //
  //   - Bytes. Six more entries here measured 549 (~91 each), putting the
  //     viewer-request bundle at 4,100 composed against the 3,674-byte budget
  //     in `packages/infra/src/web/edge.ts`. One fits; two do not.
  //   - Cache warmth, which is the budget's actual purpose. Over the 90 days to
  //     2026-08-25, `/audio` took 1,612 pageviews — ~90% of the six routes'
  //     combined traffic, and on a par with `/pricing`'s own 1,480. The other
  //     five took 50, 45, 40, 31 and 15 (`/edu`, `/youtube`, `/college`,
  //     `/ref`, `/newsletter`). Splitting ~0.5 pageviews/day six ways is a
  //     guaranteed cold miss on every request, so they would not have been
  //     registered at any byte price.
  //
  // Unregistered, those five render the FALLBACK arm (`marketingPricingForArm`
  // with no arm → the top live arm, `pricing-test-v2:price-59-trial`): $59/$4.92
  // with the trial, and the C4-safe direction — an unregistered page can only
  // ever advertise at or above what any assignable arm charges.
  '/_marketing/pricing': {
    pattern: withLocalePrefix(/^\/pricing\/?$/i),
    experiments: PRICING_TEST_ONLY,
  },
  // Academic landing page — the `academic-landing-funnel-v1` experiment runs
  // on BOTH of its URLs. `/academic` is the SEO/organic one (it existed until
  // `31caf4428` deleted it as a duplicate; `redirects.ts` has pointed
  // `/academic-old` at it the whole time) and `/fb/academic` is the Meta-ads
  // one inherited from the original Astro site. They render the same page, so
  // they must bucket the same way — otherwise the arm a visitor sees depends
  // on which URL the ad happened to use, which is the confound the experiment
  // exists to remove.
  //
  // The onboarding steps the treatment CTAs lead to (`/onboarding/$step`) are
  // intentionally NOT registered — they stay private-cached so
  // `Analytics.Bootstrap` runs and the per-step events carry a distinctId.
  // `/audio` — the one `FeaturesCardSection` route that earns a registration
  // (the reasoning, and the five that did not, are in the `/pricing` note
  // above). Not country-keyed: the features card states a price but renders no
  // free-plan copy, so it costs 2 entries, not 4. It is the second and last
  // surface where an enrolled visitor sees their own arm.
  '/_marketing/_seo/_legacy/audio': {
    pattern: withLocalePrefix(/^\/audio\/?$/i),
    experiments: PRICING_TEST_ONLY,
  },
  '/_marketing/_seo/academic': {
    pattern: withLocalePrefix(/^\/academic\/?$/i),
    experiments: ['academicLandingFunnelV1'],
  },
  '/_marketing/fb/academic': {
    pattern: withLocalePrefix(/^\/fb\/academic\/?$/i),
    experiments: ['academicLandingFunnelV1'],
  },
} as const satisfies Partial<
  Record<keyof FileRoutesById, EdgeExperimentRouteEntry>
>;

/**
 * Entries that are `cdn-shared` for caching only. Kept in a separate literal
 * the edge bundle never reaches (it is referenced only from `@__PURE__`
 * expressions below, which rolldown drops from the edge graph). The
 * `experiments: readonly []` constraint makes starting an experiment here a
 * compile error — move the entry into `edgeExperimentRoutes` instead, or the
 * edge would never stamp its variant and the flag would silently read
 * `default` for everyone.
 */
const cacheOnlyExperimentalRoutes = {
  '/_marketing/doc/': {
    pattern: /^\/doc\/?$/i,
    cacheMode: 'cdn-shared',
    experiments: [],
  },
  // Also matches `/doc/<category-slug>` landing pages; they are filtered out
  // by `routeExcludePatterns` in `./experimental-route-exclusions`.
  '/_marketing/doc/$docId': {
    pattern: /^\/doc\/[^/]+\/?$/i,
    cacheMode: 'cdn-shared',
    experiments: [],
  },
  '/_marketing/blog/': {
    pattern: /^\/blog\/?$/i,
    cacheMode: 'cdn-shared',
    experiments: [],
  },
  '/_marketing/blog/$slug': {
    pattern: /^\/blog\/[^/]+\/?$/i,
    cacheMode: 'cdn-shared',
    experiments: [],
  },
} as const satisfies Partial<
  Record<
    keyof FileRoutesById,
    RouteAnalyticsEntry & { experiments: readonly [] }
  >
>;

/** Every entry of an `edgeExperimentRoutes`-shaped literal, cdn-shared. */
type WithCdnShared<T> = {
  readonly [K in keyof T]: T[K] & { readonly cacheMode: 'cdn-shared' };
};

/**
 * Put the `cacheMode` back that {@link EdgeExperimentRouteEntry} leaves off.
 *
 * The cast is the price of the byte saving and is narrow: the loop writes
 * `cacheMode: 'cdn-shared'` onto every key of `entries` and nothing else, which
 * is exactly what `WithCdnShared<T>` says — TypeScript just cannot follow a
 * mapped type through an index-signature accumulator. The `satisfies` on
 * `experimentalRoutes` below re-checks the result against the full
 * `RouteAnalyticsEntry` shape, so a mistake here fails the typecheck there.
 */
function withCdnShared<T extends Record<string, EdgeExperimentRouteEntry>>(
  entries: T,
): WithCdnShared<T> {
  const result: Record<string, unknown> = {};
  for (const [routeId, entry] of Object.entries(entries)) {
    result[routeId] = { ...entry, cacheMode: 'cdn-shared' };
  }
  return result as WithCdnShared<T>;
}

// The `@__PURE__` IIFE keeps the merged registry (and through it the
// cache-only literal and `withCdnShared`) out of the edge bundle, which imports
// this module for `edgeExperimentsByPathPattern` only — same trick as
// `sharedCachedPathPatterns` below.
export const experimentalRoutes = /* @__PURE__ */ (() => {
  return {
    ...withCdnShared(edgeExperimentRoutes),
    ...cacheOnlyExperimentalRoutes,
  };
})() satisfies Partial<Record<keyof FileRoutesById, RouteAnalyticsEntry>>;

/**
 * Patterns for every `cdn-shared` route. Consumed by `__root.tsx`'s
 * `beforeLoad` to decide whether to skip `Analytics.Bootstrap`.
 *
 * Why pathname matching instead of `ctx.matches[i].staticData.analytics`:
 * reading `staticData.analytics?.cacheMode` from `ctx.matches` collapses
 * TanStack's whole-app route-tree inference — `ParamsReducerFn` and
 * `MakeRequiredSearchParams` lose their bindings, search params become
 * `never`, `NavigateOptions` lose required keys across unrelated route
 * files. Reproduced and reverted. Pattern-matching is the supported
 * escape hatch and the entire reason these patterns are first-class
 * fields on the registry.
 */
// The `@__PURE__` IIFE lets rolldown drop this export from the edge bundle
// (which imports this module for `edgeExperimentsByPathPattern` only) — a
// bare method chain at module scope is not provably side-effect-free and
// would otherwise ship against the CloudFront Functions byte budget.
export const sharedCachedPathPatterns: ReadonlyArray<RegExp> =
  /* @__PURE__ */ (() =>
    Object.values(experimentalRoutes)
      .filter((entry) => entry.cacheMode === 'cdn-shared')
      .map((entry) => entry.pattern))();

/**
 * Path pattern → active edge experiments for the CloudFront viewer-request
 * handler.
 *
 * Carries NO exclusion patterns. Those live in
 * `./experimental-route-exclusions`, out of this module's import graph, so
 * their slug alternation stays out of the ≤3,500-byte edge bundle. That is
 * only sound because no route with an exclusion currently runs an experiment,
 * and the edge walks experiment-bearing entries exclusively — an invariant
 * asserted in `test/unit/analytics/experimental-routes.test.ts`. If you need
 * to experiment on an excluded route, that test will fail and you must decide
 * deliberately how to spend the bytes.
 *
 * `.slice()` instead of `[...entry.experiments]` because the cloudfront-js-2.0
 * runtime rejects spread (`SyntaxError: Unexpected token "..."`).
 *
 * Derived from `edgeExperimentRoutes`, NOT the merged `experimentalRoutes` —
 * deriving from the merge would drag every cache-only entry's pattern into
 * the byte-budgeted edge bundle just to filter it out at runtime.
 *
 * No filter clause: every entry of that literal is cdn-shared and carries at
 * least one experiment BY TYPE (see {@link EdgeExperimentRouteEntry}), so both
 * predicates were constant-true checks the edge paid to evaluate.
 */
export const edgeExperimentsByPathPattern: ReadonlyArray<{
  pattern: RegExp;
  experiments: ReadonlyArray<string>;
}> = Object.values(edgeExperimentRoutes).map((entry) => ({
  pattern: entry.pattern,
  experiments: entry.experiments.slice(),
}));

/**
 * Resolve which edge-bucketed experiments are active on a given request
 * pathname. Used by `__root.tsx`'s `beforeLoad` to feed `resolveEdgeFlags`.
 *
 * Returns the first matching entry's experiments array — routes are
 * mutually exclusive by construction (the edge function does the same
 * "first match wins" walk on the same data). Empty array if no entry
 * matches the path (non-experimental route).
 *
 * The cast is safe: every entry's `experiments` is constrained by
 * `RouteAnalyticsEntry`'s conditional type — for `cacheMode: 'cdn-shared'`
 * the array elements must be `EdgeBucketedFlagKey`.
 */
export function getRouteExperiments(
  pathname: string,
): ReadonlyArray<EdgeBucketedFlagKey> {
  for (const entry of Object.values(experimentalRoutes)) {
    if (entry.cacheMode !== 'cdn-shared') continue;
    if (entry.pattern.test(pathname)) {
      return entry.experiments as ReadonlyArray<EdgeBucketedFlagKey>;
    }
  }
  return [];
}

// `isExperimentalPathExcluded` lives in `./experimental-route-exclusions` —
// it needs the public-doc slug alternation, which must not enter this
// module's import graph (see the header note on the edge bundle budget).

// =============================================================================
// CACHE-ENTRY BUDGET
//
// Nothing below is reachable from the edge bundle: the CloudFront handler
// imports `edgeExperimentsByPathPattern` and nothing else, and plain function
// declarations tree-shake out the way `getRouteExperiments` already does. The
// byte count is asserted in `route-cache-budget.test.ts`.
// =============================================================================

/**
 * Ceiling on the CloudFront cache entries a single path may fragment into.
 *
 * 8 was the edge doc's own number, kept but re-derived: §2 of
 * `docs/projects/complete/a-b-test-public-doc.md` capped concurrent
 * experiments at 3 because "2^3 = 8 cache variants per path is manageable;
 * 2^5 = 32 starts fragmenting cache hit rate noticeably". 8 was always the
 * real limit and 3 was a proxy for it that only holds while every experiment
 * is binary and fully enrolled. The binding constraint is hit-rate dilution on
 * low-traffic long-tail pages — each entry has to earn its own warmth — not an
 * AWS quota, so this is a judgement to revisit per route, not a hard limit.
 *
 * **Raised to 12 for `pricing-test-v1`, and the trade is real.** `/pricing`
 * cannot render the arm it is testing without both cache-key dimensions: six
 * arms, and the free-plan country bit that decides whether the tier table
 * shows a Free plan at all. 2 × 6 = 12, so 8 would have forced a choice
 * between an arm-blind pricing page and a cross-country-wrong one.
 *
 * **What is accepted in exchange, stated honestly:** `/pricing` took 1,480
 * pageviews in the 90 days to 2026-08-25 — about 16 a day, which split twelve
 * ways and spread across CloudFront POPs means most requests will MISS. This
 * raise does not buy warm entries; it buys a correct page, and the caching is
 * close to decorative while the test runs. That is the right trade only because
 * the alternative was an arm-blind or cross-country-wrong page, and only for
 * the test's duration: the cap comes back down when the winner ships and the
 * arms collapse to one.
 *
 * This is a ceiling, not a target. `/`, `/academic` and `/fb/academic` still
 * sit at 4 and `/audio` at 6; the higher cap must not be read as licence to add
 * a second experiment to any of them (`route-cache-budget.test.ts` freezes what
 * each path actually costs, so a drift shows up as a diff either way).
 */
export const MAX_CACHE_ENTRIES_PER_PATH = 12;

/** The two fields of an experiment definition that move the cache cost. */
export type ExperimentCacheShape = {
  readonly variantCount: number;
  /** Fraction of traffic enrolled ([0, 1]) — see `BucketingConfig`. */
  readonly enrollment: number;
};

/**
 * How many CloudFront cache entries one path fragments into.
 *
 * The cache key is `x-open-next-cache-key, x-forwarded-host, x-variants,
 * x-free-plan-country`. The first two are constant for a given path and host,
 * so the entry count is the number of distinct `(x-variants,
 * x-free-plan-country)` pairs `handler-request.ts` can stamp on that path:
 *
 *     entries = countryStates × Π over experiments of variantStates
 *
 * `x-variants` is `key=variant` pairs in route-declaration order with
 * **non-enrolled experiments omitted entirely** (handler step 4), so each
 * experiment contributes independently:
 *
 * - `enrollment: 1` — every human is enrolled, so the key is always present
 *   carrying one of its variants: `variantCount` states.
 * - `enrollment < 1` — a non-enrolled viewer's key is *absent*, which is a
 *   different header value from every enrolled one (down to `x-variants: ""`
 *   when every experiment on the path omits): `variantCount + 1` states.
 *
 * **Crawlers cost nothing on top of that**, which is worth stating because the
 * bot pinning added in M2 looks like it should. A bot takes every experiment's
 * `fallbackVariant`, and `fallbackVariant` is required to be one of that
 * experiment's own variants (pinned in `edge-bot-pinning.test.ts`) — so the
 * bot's all-keys-present header is one an enrolled human can produce too, and
 * it lands on an entry the product already counts. The `+1` at
 * `enrollment < 1` is the omission state, not the bot.
 *
 * The one shape this over-counts is `enrollment: 0`, where no human is ever
 * enrolled and the only entries are the omitted one and the bot's fallback.
 * An experiment nobody is in is not a thing we ship, and over-counting is the
 * safe direction for a budget.
 *
 * `x-free-plan-country` is `'0' | '1'` on paths that render country-dependent
 * free-plan copy and a constant `''` everywhere else, so it doubles the count
 * on exactly those paths — which today are exactly the three paths that run
 * experiments. Leaving it out would under-report the live cost by 2×.
 */
export function cacheEntriesPerPath(input: {
  readonly experiments: ReadonlyArray<ExperimentCacheShape>;
  readonly countryKeyed: boolean;
}): number {
  let entries = input.countryKeyed ? 2 : 1;
  for (const experiment of input.experiments) {
    entries *= experiment.variantCount + (experiment.enrollment < 1 ? 1 : 0);
  }
  return entries;
}

/**
 * Look up what one registered experiment costs, by flag name.
 *
 * Throws rather than skipping an unknown name: a name that resolves to no
 * definition means the registry and the flag module disagree, and silently
 * counting it as free is how a route ends up over budget with a green test.
 * `EdgeBucketedFlagKey` makes that unreachable at compile time.
 */
export function edgeExperimentCacheShape(
  name: EdgeBucketedFlagKey,
): ExperimentCacheShape {
  const def = (
    EdgeClientFlags as Record<string, EdgeFeatureFlagDef | undefined>
  )[name];
  if (def === undefined) {
    throw new Error(`no edge flag definition for experiment "${name}"`);
  }
  return {
    variantCount: def.variants.length,
    enrollment: def.bucketing.enrollment,
  };
}
